import React, { useEffect, useState, useRef, useCallback } from "react";
import * as signalR from "@microsoft/signalr";
import { connection } from "./signalr";
import axios from "axios";
import ApiConfig from "../../utils/ApiConfig";

export default function ChatWindow({
  activeChatName,
  user,
  activeChat,
  messages,
  setMessages
}) {
  // =========================================================
  // REFS & STATE
  // =========================================================
  const peerConnection = useRef(null);
  const localVideoRef = useRef(null);
  const remoteVideoRef = useRef(null);
  const remoteAudioRef = useRef(null);
  const localStreamRef = useRef(null);
  const iceCandidateQueue = useRef([]);

  const [text, setText] = useState("");
  const [callType, setCallType] = useState(null);
  const [callId, setCallId] = useState(null);
  const [callStatus, setCallStatus] = useState("");

  const [localStream, setLocalStream] = useState(null);
  const [remoteStream, setRemoteStream] = useState(null);
  const [incomingCall, setIncomingCall] = useState(null);

  useEffect(() => {
    if (localVideoRef.current) {
      localVideoRef.current.srcObject = localStream ? localStream : null;
    }
  }, [localStream, callType]);

  useEffect(() => {
    if (remoteVideoRef.current) {
      remoteVideoRef.current.srcObject = remoteStream ? remoteStream : null;
    }
    if (remoteAudioRef.current) {
      remoteAudioRef.current.srcObject = remoteStream ? remoteStream : null;
    }
  }, [remoteStream, callType]);

  // =========================================================
  // CALL DATA API
  // =========================================================
  const saveCallData = async ({ id = 0, type = null, status = null, action }) => {
    try {
      const request = {
        id: Number(id) || 0,
        user_Id: Number(user),
        opponent_Id: Number(activeChat),
        callType: type,
        status: status,
        action: action
      };

      const response = await axios.post(
        ApiConfig.getUrl("api/chat/CallData"),
        request
      );

      return response.data;
    } catch (error) {
      console.error("CallData API Error:", error.response?.data || error.message);
      throw error;
    }
  };

  const getCallIdFromResponse = (response) => {
    return (
      response?.Id ??
      response?.id ??
      response?.response?.Id ??
      response?.response?.id ??
      response?.data?.Id ??
      response?.data?.id ??
      null
    );
  };

  // =========================================================
  // SIGNALR HELPER
  // =========================================================
  const waitForSignalR = async (timeout = 10000) => {
    const startTime = Date.now();
    while (Date.now() - startTime < timeout) {
      if (connection.state === signalR.HubConnectionState.Connected) {
        return true;
      }
      if (connection.state === signalR.HubConnectionState.Disconnected) {
        try {
          await connection.start();
          return true;
        } catch (error) {
          console.error("SignalR Start Error:", error);
          return false;
        }
      }
      await new Promise((resolve) => setTimeout(resolve, 200));
    }
    return false;
  };

  const sendCallSignal = async (opponentId, signal) => {
    try {
      const connected = await waitForSignalR();
      if (!connected) return false;

      await connection.invoke(
        "SendCallSignal",
        String(opponentId),
        JSON.stringify(signal)
      );
      return true;
    } catch (error) {
      console.error("SendCallSignal Error:", error);
      return false;
    }
  };

  // =========================================================
  // MEDIA DEVICE HANDLING
  // =========================================================
  const getMedia = async (type) => {
    try {
      const constraints = {
        audio: true,
        video: type === "video" ? true : false
      };

      const stream = await navigator.mediaDevices.getUserMedia(constraints);

      localStreamRef.current = stream;
      setLocalStream(stream);
      return stream;
    } catch (error) {
      console.error("Microphone/Camera Permission Error:", error);
      alert("Permission Error: " + error.message);
      return null;
    }
  };

  const processBufferedIceCandidates = async () => {
    if (!peerConnection.current || !peerConnection.current.remoteDescription) return;

    while (iceCandidateQueue.current.length > 0) {
      const candidate = iceCandidateQueue.current.shift();
      try {
        await peerConnection.current.addIceCandidate(new RTCIceCandidate(candidate));
      } catch (e) {
        console.error("Error adding queued ICE Candidate:", e);
      }
    }
  };

  // =========================================================
  // WEBRTC PEER INITIALIZATION
  // =========================================================
  const createPeerConnection = (targetUserId) => {
    const configuration = {
      iceServers: [
        { urls: "stun:stun.l.google.com:19302" },
        { urls: "stun:stun1.l.google.com:19302" },

        // Metered.ca;
        {
          urls: "turn:global.turn.metered.ca:80",
          username: "YOUR_METERED_USERNAME", // Metered Username
          credential: "YOUR_METERED_PASSWORD"  // Metered Password
        },
        {
          urls: "turns:global.turn.metered.ca:443?transport=tcp",
          username: "YOUR_METERED_USERNAME",
          credential: "YOUR_METERED_PASSWORD"
        }
      ]
    };

    const pc = new RTCPeerConnection(configuration);

    // 1. Send ICE Candidate
    pc.onicecandidate = async (event) => {
      if (event.candidate && targetUserId) {
        await sendCallSignal(targetUserId, {
          type: "ice-candidate",
          candidate: event.candidate
        });
      }
    };

    // 2. Handle Remote Audio/Video Stream
    pc.ontrack = (event) => {
      if (event.streams && event.streams[0]) {
        setRemoteStream(event.streams[0]);
      }
    };

    // 3. Monitor Connection Status
    pc.onconnectionstatechange = () => {
      console.log("WebRTC Connection State:", pc.connectionState);

      if (pc.connectionState === "connected") {
        setCallStatus("Connected");
      } else if (pc.connectionState === "failed") {
        setCallStatus("Connection Failed");
      } else if (pc.connectionState === "disconnected") {
        setCallStatus("Disconnected");
      }
    };

    peerConnection.current = pc;
    return pc;
  };

  // =========================================================
  // CALL CONTROLS
  // =========================================================
  const startCall = async (type) => {
    try {
      const connected = await waitForSignalR();
      if (!connected) {
        alert("Call connection is not ready. Please try again.");
        return;
      }

      const response = await saveCallData({ id: 0, type, status: null, action: "START" });
      const newCallId = getCallIdFromResponse(response);

      if (!newCallId) {
        alert("Call could not be started.");
        return;
      }

      setCallId(newCallId);
      setCallType(type);
      setCallStatus("Calling...");

      const stream = await getMedia(type);
      if (!stream) {
        setCallType(null);
        setCallId(null);
        setCallStatus("");
        return;
      }

      // FIX 1: activeChat Parameter Pass பண்ணப்பட்டுள்ளது
      const pc = createPeerConnection(activeChat);
      stream.getTracks().forEach((track) => pc.addTrack(track, stream));

      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);

      await sendCallSignal(activeChat, {
        type: "offer",
        sender: String(user),
        callId: newCallId,
        callType: type,
        offer: offer
      });
    } catch (error) {
      console.error("Start Call Error:", error);
    }
  };

  const acceptCall = async () => {
    if (!incomingCall) return;

    try {
      const connected = await waitForSignalR();
      if (!connected) return;

      const { callType: type, callId: incomingCallId, offer, sender } = incomingCall;
      const targetUser = sender || activeChat;

      await saveCallData({ id: incomingCallId, type, status: null, action: "ACCEPT" });

      setCallId(incomingCallId);
      setCallType(type);
      setCallStatus("Connecting...");

      const stream = await getMedia(type);
      if (!stream) return;

      // FIX 2: targetUser Parameter Pass பண்ணப்பட்டுள்ளது
      const pc = createPeerConnection(targetUser);
      stream.getTracks().forEach((track) => pc.addTrack(track, stream));

      await pc.setRemoteDescription(new RTCSessionDescription(offer));
      await processBufferedIceCandidates();

      const answer = await pc.createAnswer();
      await pc.setLocalDescription(answer);

      await sendCallSignal(targetUser, {
        type: "answer",
        callId: incomingCallId,
        answer: answer
      });

      setIncomingCall(null);
    } catch (error) {
      console.error("Accept Call Error:", error);
    }
  };

  const rejectCall = async () => {
    if (!incomingCall) return;

    try {
      const targetUser = incomingCall.sender || activeChat;
      await saveCallData({
        id: incomingCall.callId,
        type: incomingCall.callType,
        status: null,
        action: "REJECT"
      });

      await sendCallSignal(targetUser, {
        type: "call-rejected",
        callId: incomingCall.callId
      });

      setIncomingCall(null);
      setCallType(null);
      setCallId(null);
      setCallStatus("");
    } catch (error) {
      console.error("Reject Call Error:", error);
    }
  };

  const endCall = useCallback(async (notifyOpponent = true) => {
    if (notifyOpponent && callId) {
      await sendCallSignal(activeChat, { type: "call-ended", callId });
    }

    if (callId) {
      try {
        await saveCallData({ id: callId, type: callType, status: null, action: "END" });
      } catch (error) {
        console.error("End CallData Error:", error);
      }
    }

    setCallType(null);
    setCallId(null);
    setCallStatus("");
    setIncomingCall(null);
    setRemoteStream(null);
    setLocalStream(null);

    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach((track) => track.stop());
      localStreamRef.current = null;
    }

    if (peerConnection.current) {
      peerConnection.current.ontrack = null;
      peerConnection.current.onicecandidate = null;
      peerConnection.current.onconnectionstatechange = null;
      peerConnection.current.close();
      peerConnection.current = null;
    }

    iceCandidateQueue.current = [];
  }, [callId, callType, activeChat]);

  // =========================================================
  // SIGNALR EVENT SUBSCRIPTIONS
  // =========================================================
  useEffect(() => {
    let mounted = true;

    const receiveMessage = (message) => {
      if (mounted) setMessages((prev) => [...prev, message]);
    };

    const receiveCallSignal = async (data) => {
      try {
        const signal = typeof data === "string" ? JSON.parse(data) : data;
        console.log("Received Signal:", signal);

        if (signal.type === "offer") {
          setIncomingCall({
            callId: signal.callId,
            callType: signal.callType,
            offer: signal.offer,
            sender: signal.sender
          });
        } else if (signal.type === "answer") {
          if (peerConnection.current) {
            await peerConnection.current.setRemoteDescription(
              new RTCSessionDescription(signal.answer)
            );
            await processBufferedIceCandidates();
            setCallStatus("Connected");
          }
        } else if (signal.type === "ice-candidate") {
          if (
            peerConnection.current &&
            peerConnection.current.remoteDescription
          ) {
            await peerConnection.current.addIceCandidate(
              new RTCIceCandidate(signal.candidate)
            );
          } else {
            iceCandidateQueue.current.push(signal.candidate);
          }
        } else if (signal.type === "call-rejected") {
          alert("Call rejected");
          await endCall(false);
        } else if (signal.type === "call-ended") {
          await endCall(false);
        }
      } catch (error) {
        console.error("Call Signal Error:", error);
      }
    };

    const initializeSignalR = async () => {
      try {
        if (connection.state === signalR.HubConnectionState.Disconnected) {
          await connection.start();
        }

        connection.off("ReceiveMessage");
        connection.off("ReceiveCallSignal");

        connection.on("ReceiveMessage", receiveMessage);
        connection.on("ReceiveCallSignal", receiveCallSignal);

        if (connection.state === signalR.HubConnectionState.Connected && user) {
          await connection.invoke("Register", String(user));
          console.log(`User ${user} successfully registered to SignalR Hub`);
        }
      } catch (error) {
        console.error("SignalR Initialization Error:", error);
      }
    };

    initializeSignalR();

    return () => {
      mounted = false;
      connection.off("ReceiveMessage", receiveMessage);
      connection.off("ReceiveCallSignal", receiveCallSignal);
    };
  }, [user, endCall, setMessages]);

  // =========================================================
  // MESSAGE DISPATCH
  // =========================================================
  const sendMessage = async () => {
    if (!text.trim()) return;

    const msg = {
      sender: String(user),
      receiver: String(activeChat),
      message: text,
      status: "sent"
    };

    try {
      setMessages((prev) => [...prev, msg]);
      setText("");
      await axios.post(ApiConfig.getUrl("api/chat/send"), msg);
    } catch (error) {
      console.error("Send Message Error:", error);
    }
  };

  // =========================================================
  // RETURN UI
  // =========================================================
  return (
    <div
      className="chatBox"
      style={{
        backgroundColor: "white"
      }}
    >
      {/* Hidden Audio element for Audio Calls */}
      <audio ref={remoteAudioRef} autoPlay />
       <div className="chatHeader">
        <h3 className="ms-4">
          {activeChatName}
        </h3>
        </div>

      {/* <div className="chatHeader">
        <h3 className="ms-4">
          {activeChatName}
        </h3>

        <div className="callButtons">
          <button
            type="button"
            onClick={() => startCall("audio")}
            className="audioCallBtn"
            disabled={callType !== null}
          >
            <i className="bi bi-telephone"></i>
          </button>

          <button
            type="button"
            onClick={() => startCall("video")}
            className="videoCallBtn"
            disabled={callType !== null}
          >
            <i className="bi bi-camera"></i>
          </button>
        </div>
      </div> */}

      {/* INCOMING CALL POPUP */}
      {incomingCall && (
        <div className="incomingCallPopup">
          <div className="incomingCallContent">
            <div className="incomingCallIcon">
              {incomingCall.callType === "video" ? "🎥" : "📞"}
            </div>

            <h3>Incoming {incomingCall.callType} call...</h3>

            <div className="incomingCallButtons">
              <button
                type="button"
                onClick={acceptCall}
                className="acceptCallBtn"
              >
                Accept
              </button>

              <button
                type="button"
                onClick={rejectCall}
                className="rejectCallBtn"
              >
                Reject
              </button>
            </div>
          </div>
        </div>
      )}

      {/* CALL SCREEN */}
      {callType && (
        <div className="callContainer">
          {/* VIDEO CALL */}
          {callType === "video" && (
            <>
              {/* REMOTE VIDEO */}
              <video
                ref={remoteVideoRef}
                autoPlay
                playsInline
                className="remoteVideo"
              />

              {/* LOCAL VIDEO */}
              <video
                ref={localVideoRef}
                autoPlay
                muted
                playsInline
                className="localVideo"
              />
            </>
          )}

          {/* AUDIO CALL */}
          {callType === "audio" && (
            <div className="audioCallScreen">
              <div className="callUserIcon">👤</div>
              <h2>{activeChatName}</h2>
              <p>{callStatus}</p>
            </div>
          )}

          {/* CALL STATUS */}
          <div className="callStatus">
            {callStatus}
          </div>

          {/* END CALL */}
          <button
            type="button"
            onClick={() => endCall(true)}
            className="endCallButton"
          >
            📞 End
          </button>
        </div>
      )}

      {/* MESSAGES */}
      <div className="messages">
        {messages.map((m, i) => (
          <div
            key={i}
            className={
              "msg " +
              (m.sender === user ? "right" : "left") +
              " me-3 mt-2"
            }
          >
            {m.message}
            <span className="tick">
              {m.status === "sent" && "✓"}
              {m.status === "delivered" && "✓✓"}
              {m.status === "read" && "✓✓✔"}
            </span>
          </div>
        ))}
      </div>

      {/* MESSAGE INPUT */}
      <div className="inputBox">
        <input
          className="mb-5"
          style={{
            border: "1px solid var(--primary-color)"
          }}
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              sendMessage();
            }
          }}
          placeholder="Type message..."
        />

        <button
          type="button"
          style={{
            width: "100px",
            marginBottom: "50px",
            backgroundColor: "var(--primary-color)",
            color: "white"
          }}
          onClick={sendMessage}
        >
          Send
        </button>
      </div>
    </div>
  );
}